import { EngageTimeSeries, EngageTimeSeriesConfig, EngageTimeSeriesType } from '@/types/engage.types';
import { common as c } from '@pendo/aggregations';
import { http } from '@pendo/http';
import { convertTimeSeriesForAggregation, isDefaultDateRange } from './date-range.utils';
import get from 'lodash/get';
import { getFirstLastDateValues } from '@/common/time-series-picker/time-series-picker.util';
import moment from '@utils/moment-wrapper';

type TimeInterval = 'hour' | 'day' | 'week' | 'month' | 'quarter';

type TimeSeries = {
    period: c.TimeSeriesPeriodType;
    first: string | number;
    last?: string | number;
    count?: number;
};

export const getTimeIntervalFromPeriod = (period?: c.TimeSeriesPeriodType | string): TimeInterval => {
    switch (period) {
        case 'hourRange':
        case 'hourly':
            return 'hour';
        case 'dayRange':
        case 'daily':
            return 'day';
        case 'weekly':
        case 'weekRange':
            return 'week';
        case 'monthly':
        case 'monthRange':
            return 'month';
        case 'quarterly':
        case 'quarterRange':
            return 'quarter';
    }

    return 'day';
};

export const getPeriodForTimeInterval = ({
    timeInterval,
    isPeriodCalculation,
}: {
    timeInterval: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'aggregate' | 'today' | 'yesterday';
    isPeriodCalculation: boolean;
}) => {
    if (timeInterval === 'hour') {
        if (isPeriodCalculation) {
            return 'hourly';
        }

        return 'hourRange';
    }
    if (timeInterval === 'day') {
        if (isPeriodCalculation) {
            return 'daily';
        }

        return 'dayRange';
    }
    if (timeInterval === 'week') {
        if (isPeriodCalculation) {
            return 'weekly';
        }

        return 'weekRange';
    }
    if (timeInterval === 'month') {
        if (isPeriodCalculation) {
            return 'monthly';
        }

        return 'monthRange';
    }
    if (timeInterval === 'quarter') {
        if (isPeriodCalculation) {
            return 'quarterly';
        }

        return 'quarterRange';
    }

    return isPeriodCalculation ? 'daily' : 'dayRange';
};

export const convertPeriodToRangeTypePeriod = (period: c.TimeSeriesPeriodType) => {
    const timeInterval = getTimeIntervalFromPeriod(period);

    return getPeriodForTimeInterval({ timeInterval, isPeriodCalculation: false });
};

export type PossiblePeriodsTimeSeries = TimeSeries | c.TimeSeries;

export const fetchPossiblePeriods = (timeSeries: PossiblePeriodsTimeSeries, excludeWeekends: boolean = false) => {
    const possiblePeriodsApi = '/api/s/_SID_/possiblePeriods';

    return http.post(possiblePeriodsApi, { ...timeSeries, excludeWeekends });
};

const handleTodayCase = (period: string) => {
    const isHourly = period === 'hourRange' || period === 'hourly';

    return {
        period: period as c.TimeSeriesPeriodType,
        first: 'startOfPeriod("daily",now())',
        count: isHourly ? 24 : 1,
    };
};

const handleYesterdayCase = (period: string) => {
    const isHourly = period === 'hourRange' || period === 'hourly';

    return {
        period: period as c.TimeSeriesPeriodType,
        first: 'dateAdd(startOfPeriod("daily",now()),-1,"days")',
        count: isHourly ? 24 : 1,
    };
};

const handleSingleDayCustomCase = (period: string, startDate: string) => {
    const isHourly = period === 'hourRange' || period === 'hourly';

    return {
        period: period as c.TimeSeriesPeriodType,
        first: `startOfPeriod("daily",date("${startDate}"))`,
        count: isHourly ? 24 : 1,
    };
};

export const rectifyDateTypes = (
    dateRange: EngageTimeSeries | { timeSeries: EngageTimeSeries } | Pick<EngageTimeSeriesConfig, 'type'>,
    period: string,
    customDateRangeEndInclusive = false,
) => {
    let simplifiedTimeSeries: TimeSeries | undefined;
    let dateRangeType;
    if ('type' in dateRange) {
        // Special case handling for widgets that are still using the default date format from widget settings.
        const { type } = dateRange;
        dateRangeType = type;
        if (type && /Last (\d)* Day(s)?/.test(type)) {
            const firstAndLast = getFirstLastDateValues(type, period);
            if (firstAndLast) {
                simplifiedTimeSeries = firstAndLast as TimeSeries;
            }
        } else if (type === 'Custom' && 'start' in dateRange && 'end' in dateRange) {
            const endDate = moment(dateRange.end as string, { ignoreTimeZone: true });
            const startDate = moment(dateRange.start as string, { ignoreTimeZone: true });
            const count = moment(endDate).diff(moment(startDate), 'days');
            if (count > 1 && period !== 'hourly' && period !== 'hourRange') {
                simplifiedTimeSeries = {
                    first: `date("${dateRange.start}")`,
                    // Picker end is midnight on the day after the selection; skip the adjustment when customDateRangeEndInclusive is true.
                    last: customDateRangeEndInclusive
                        ? `date("${dateRange.end}")`
                        : `dateAdd(date("${dateRange.end}"), -1, "days")`,
                    period: period as c.TimeSeriesPeriodType,
                };
            }
        }
    }

    if (!simplifiedTimeSeries && 'config' in dateRange) {
        dateRangeType = dateRange.config.type;
        if (dateRangeType === 'Today') {
            simplifiedTimeSeries = handleTodayCase(period);
        } else if (dateRangeType === 'Yesterday') {
            simplifiedTimeSeries = handleYesterdayCase(period);
        } else if (dateRangeType === 'Custom' && dateRange.spec?.count === 1) {
            simplifiedTimeSeries = handleSingleDayCustomCase(period, dateRange.config.start);
        } else {
            // Calculate the agg time series using the latest period from widget settings, not the previous value on time series.
            const modifiedTimeSeries = {
                config: { ...dateRange.config },
                label: dateRange.label,
                spec: { ...dateRange.spec, period },
            };

            simplifiedTimeSeries = convertTimeSeriesForAggregation(modifiedTimeSeries, true);
        }
    }

    if (!simplifiedTimeSeries && 'timeSeries' in dateRange && isDefaultDateRange(get(dateRange, 'timeSeries'))) {
        const { timeSeries } = dateRange;
        dateRangeType = timeSeries.config.type;
        if (dateRangeType === 'Today') {
            simplifiedTimeSeries = handleTodayCase(period);
        } else if (dateRangeType === 'Yesterday') {
            simplifiedTimeSeries = handleYesterdayCase(period);
        } else if (dateRangeType === 'Custom' && timeSeries.spec?.count === 1) {
            simplifiedTimeSeries = handleSingleDayCustomCase(period, timeSeries.config.start);
        } else {
            // Calculate the agg time series using the latest period from widget settings, not the previous value on time series.
            const modifiedTimeSeries = {
                config: { ...timeSeries.config },
                label: timeSeries.label,
                spec: { ...timeSeries.spec, period },
            };

            simplifiedTimeSeries = convertTimeSeriesForAggregation(modifiedTimeSeries, true);
        }
    }

    return { simplifiedTimeSeries, dateRangeType };
};

export const adjustTimeSeriesForSettings = ({
    timeSeries,
    completePeriodsOnly,
    weekendsHidden,
    period,
    dateRangeType,
    isPossiblePeriodsCalculation,
}: {
    timeSeries?: TimeSeries;
    completePeriodsOnly: boolean;
    weekendsHidden: boolean;
    period: c.TimeSeriesPeriodType;
    dateRangeType?: EngageTimeSeriesType;
    isPossiblePeriodsCalculation: boolean;
}) => {
    if (timeSeries) {
        const isRelativeDateRange = dateRangeType && /Last (\d)* Day(s)?/.test(dateRangeType);
        const adjustedTimeSeries = { ...timeSeries };
        const timeInterval = getTimeIntervalFromPeriod(period);
        const timeIntervalRange = `${timeInterval}Range`;

        if (timeInterval === 'day') {
            adjustedTimeSeries.first = `startOfPeriod("${timeIntervalRange}", ${timeSeries.first})`;
        } else if (timeInterval === 'hour') {
            adjustedTimeSeries.first = `startOfPeriod("${timeIntervalRange}", ${timeSeries.first})`;

            return adjustedTimeSeries;
        } else {
            const pluralTimeInterval = `${timeInterval}s`;
            if (completePeriodsOnly) {
                adjustedTimeSeries.first = `startOfPeriod("${timeIntervalRange}", dateAdd(dateAdd(${timeSeries.first}, 1, "${pluralTimeInterval}"), -1, "days"))`;
            } else {
                adjustedTimeSeries.first = `startOfPeriod("${timeIntervalRange}", ${timeSeries.first})`;
            }

            if (completePeriodsOnly) {
                adjustedTimeSeries.last = `lastDayOfPeriod("${timeIntervalRange}", startOfPeriod("${timeIntervalRange}", dateAdd(dateAdd(${timeSeries.last}, -1, "${pluralTimeInterval}"), 1, "days")))`;
            } else if (!isRelativeDateRange) {
                adjustedTimeSeries.last = `dateAdd(dateAdd(startOfPeriod("${timeIntervalRange}", ${timeSeries.last}), 1, "${pluralTimeInterval}"), -1, "days")`;
            }
        }

        let finalPeriod = period;
        if (isPossiblePeriodsCalculation) {
            if (timeInterval === 'day') {
                finalPeriod = 'daily';
            }
        } else if (weekendsHidden) {
            finalPeriod = 'dayRange';
        }

        return {
            ...adjustedTimeSeries,
            period: finalPeriod,
        };
    }

    return undefined;
};

export const calculateTimeSeries = ({
    dateRange,
    completePeriodsOnly,
    weekendsHidden,
    period,
    isPossiblePeriodsCalculation,
    customDateRangeEndInclusive,
}: {
    dateRange: EngageTimeSeries | { timeSeries: EngageTimeSeries } | Pick<EngageTimeSeriesConfig, 'type'>;
    completePeriodsOnly: boolean | undefined;
    weekendsHidden: boolean | undefined;
    period: c.TimeSeriesPeriodType;
    isPossiblePeriodsCalculation: boolean;
    customDateRangeEndInclusive?: boolean;
}) => {
    const { simplifiedTimeSeries, dateRangeType } = rectifyDateTypes(
        dateRange,
        period,
        customDateRangeEndInclusive,
    );

    if (completePeriodsOnly || weekendsHidden) {
        return adjustTimeSeriesForSettings({
            timeSeries: simplifiedTimeSeries,
            period: period as c.TimeSeriesPeriodType,
            dateRangeType,
            completePeriodsOnly: !!completePeriodsOnly,
            weekendsHidden: !!weekendsHidden,
            isPossiblePeriodsCalculation,
        });
    }

    return simplifiedTimeSeries;
};

export const setupTimeSeries = ({
    isPeriodCalculation,
    completePeriodsOnly,
    weekendsHidden,
    dateRange,
    timeInterval,
    customDateRangeEndInclusive,
}: {
    isPeriodCalculation: boolean;
    completePeriodsOnly: boolean;
    weekendsHidden: boolean;
    dateRange: EngageTimeSeries | { timeSeries: EngageTimeSeries } | EngageTimeSeriesConfig;
    timeInterval: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'aggregate' | 'today' | 'yesterday';
    customDateRangeEndInclusive?: boolean;
}): TimeSeries | undefined => {
    let timeSeries: TimeSeries | undefined;
    if (timeInterval === 'today') {
        timeSeries = {
            period: isPeriodCalculation ? 'daily' : 'dayRange',
            first: 'startOfPeriod("daily",now())',
            count: 1,
        };
    } else if (timeInterval === 'yesterday') {
        timeSeries = {
            period: isPeriodCalculation ? 'daily' : 'dayRange',
            first: 'dateAdd(startOfPeriod("daily",now()),-1,"days")',
            count: 1,
        };
    } else {
        const period = getPeriodForTimeInterval({ timeInterval, isPeriodCalculation });
        timeSeries = calculateTimeSeries({
            period,
            dateRange,
            completePeriodsOnly,
            weekendsHidden,
            isPossiblePeriodsCalculation: isPeriodCalculation,
            customDateRangeEndInclusive,
        });
    }

    return timeSeries;
};
